import React, { useState } from 'react';
import { t } from '../i18n';
import UnifiedLauncherButton from './UnifiedLauncherButton';
import { useBreakpoint } from '../hooks/useBreakpoint';

/**
 * LauncherWidget Component
 * 
 * Floating chat launcher that appears in the bottom-right corner.
 * Uses the unified SVG button for a cohesive, integrated design.
 */
const LauncherWidget = ({ onOpenChat, onCloseChat, isChatOpen, isOnline = true, language }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const { isMobile } = useBreakpoint();

  const handleMouseDown = () => setIsPressed(true);
  const handleMouseUp = () => setIsPressed(false);

  const handleClick = () => {
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 150);

    if (isChatOpen) {
      onCloseChat();
    } else {
      onOpenChat();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: isMobile ? '16px' : '24px',
        right: isMobile ? '16px' : '24px',
        zIndex: 1000,
      }}
    >
      {/* Tooltip Message - shows when chat is closed */}
      {!isChatOpen && (
        <div
          style={{
            position: 'absolute',
            right: isMobile ? '68px' : '72px',
            top: '50%',
            background: 'var(--bg-primary)',
            padding: isMobile ? '10px 14px' : '12px 16px',
            borderRadius: isMobile ? '12px' : '14px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
            border: '1px solid var(--border-subtle)',
            whiteSpace: 'nowrap',
            opacity: 1,
            transform: 'translateY(-50%)',
            transition: 'all var(--duration-normal) var(--ease-out)',
            cursor: 'pointer',
            zIndex: 1001,
          }}
          onClick={handleClick}
        >
          <p style={{
            margin: 0,
            fontSize: isMobile ? '14px' : '15px',
            color: 'var(--text-primary)',
            fontWeight: '500',
            letterSpacing: '0.003em',
            lineHeight: '1.5',
          }}>
            {t('tooltip_howCanIHelp', language)}
          </p>
          
          {/* Arrow pointing to launcher */}
          <div
            style={{
              position: 'absolute',
              right: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid var(--bg-primary)',
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
            }}
          />
        </div>
      )}

      {/* Unified Launcher Button - Single SVG with all elements */}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={() => setIsPressed(true)}
        onTouchEnd={() => setIsPressed(false)}
        style={{ touchAction: 'manipulation' }}
      >
        <UnifiedLauncherButton
          size={isMobile ? 56 : 60}
          onClick={handleClick}
          isPressed={isPressed}
          isHovered={isHovered}
        />
      </div>
    </div>
  );
};

export default LauncherWidget;
